import { useCallback, useEffect, useRef, useState } from 'react';
import { sharedUnderstanding } from '@/speech/understand/understanding';
import { DEFAULT_LINES, MenuConversation, type MenuLines } from './conversation';
import type { Menu, Rank } from './decide';
import { useListening } from './useListening';

/** The classifier, once it is loaded; null before (the rules decide alone until then). */
const rank: Rank = async (question, labels, text) => {
  const u = sharedUnderstanding();
  if (!u.canRank) return null;
  return u.rank(question, labels, text);
};

/**
 * A voice menu on a page (docs/design/voice-control.md): while `menu` is set and the microphone
 * is on, what the child says picks an option (`onPick`), or the game answers with a line
 * (`onSay`: a check-back, the options named again, or the answer to a question). Tapping the
 * same options works without any of this. A new menu object with the same question and options
 * keeps the conversation (and any check-back).
 */
export function useVoiceMenu(
  menu: Menu | null,
  onPick: (id: string) => void,
  onSay: (line: string) => void,
  lines: MenuLines = DEFAULT_LINES,
) {
  const conversation = useRef<MenuConversation | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const callbacks = useRef({ onPick, onSay });
  useEffect(() => {
    callbacks.current = { onPick, onSay };
  }, [onPick, onSay]);

  // The menu counts as changed only when its question or options do, not when a page rebuilds
  // the same menu as a new object (that would drop a pending check-back).
  const menuKey = menu ? JSON.stringify([menu.question, menu.options.map((o) => o.id)]) : '';
  const latest = useRef(menu);
  useEffect(() => {
    latest.current = menu;
  }, [menu]);
  useEffect(() => {
    const current = latest.current;
    if (!current) {
      conversation.current = null;
      return;
    }
    if (conversation.current) conversation.current.setMenu(current);
    else
      conversation.current = new MenuConversation(
        current,
        rank,
        (text) => sharedUnderstanding().answer(text),
        lines,
      );
  }, [menuKey, lines]);

  const onHeard = useCallback(async (text: string) => {
    const c = conversation.current;
    if (!c) return;
    const reply = await c.hear(text);
    setConfirming(c.confirming?.id ?? null);
    if (reply.say) callbacks.current.onSay(reply.say);
    if (reply.pick) callbacks.current.onPick(reply.pick);
  }, []);

  const listening = useListening(onHeard);
  return { ...listening, confirming };
}
